import { Fragment, useEffect } from 'react'
import { Transition } from '@headlessui/react'
import { CheckCircle, XCircle, Info, X } from 'lucide-react'

/* ==========================================================================
   Toast Component
   Based on design.md specifications
   Uses Headless UI Transition for slide-up + fade animation
   ========================================================================== */

export type ToastVariant = 'success' | 'error' | 'info'

export interface ToastProps {
  /** Unique identifier for the toast */
  id: string
  /** Toast variant determines colors and icon */
  variant: ToastVariant
  /** Toast message content */
  message: string
  /** Whether the toast is visible */
  visible: boolean
  /** Callback when toast should be dismissed */
  onDismiss: (id: string) => void
  /** Auto-dismiss duration in ms (default: 5000) */
  duration?: number
}

const variantStyles: Record<ToastVariant, { bg: string; icon: string }> = {
  success: {
    bg: 'bg-[var(--color-success)]',
    icon: 'text-white',
  },
  error: {
    bg: 'bg-[var(--color-error)]',
    icon: 'text-white',
  },
  info: {
    bg: 'bg-charcoal',
    icon: 'text-white',
  },
}

const variantIcons: Record<ToastVariant, typeof CheckCircle> = {
  success: CheckCircle,
  error: XCircle,
  info: Info,
}

/**
 * Individual toast notification with slide-up animation.
 * Auto-dismisses after configurable duration.
 *
 * @example
 * ```tsx
 * <Toast
 *   id="1"
 *   variant="success"
 *   message="Operation completed successfully"
 *   visible={true}
 *   onDismiss={(id) => removeToast(id)}
 * />
 * ```
 */
export function Toast({
  id,
  variant,
  message,
  visible,
  onDismiss,
  duration = 5000,
}: ToastProps) {
  const styles = variantStyles[variant]
  const Icon = variantIcons[variant]

  // Auto-dismiss after duration
  useEffect(() => {
    if (!visible || duration <= 0) return

    const timer = setTimeout(() => {
      onDismiss(id)
    }, duration)

    return () => clearTimeout(timer)
  }, [id, visible, duration, onDismiss])

  return (
    <Transition
      show={visible}
      as={Fragment}
      enter="transform ease-out duration-300 transition"
      enterFrom="translate-y-2 opacity-0"
      enterTo="translate-y-0 opacity-100"
      leave="transition ease-in duration-150"
      leaveFrom="opacity-100 translate-y-0"
      leaveTo="opacity-0 translate-y-2"
    >
      <div
        className={`flex items-center gap-3 px-4 py-3 sm:px-6 sm:py-4 rounded-lg shadow-[0_4px_12px_rgba(0,0,0,0.15)] ${styles.bg}`}
        role="alert"
        aria-live="polite"
      >
        <Icon
          className={`h-5 w-5 flex-shrink-0 ${styles.icon}`}
          aria-hidden="true"
        />
        <p className="flex-1 text-sm text-white font-medium">{message}</p>
        <button
          type="button"
          onClick={() => onDismiss(id)}
          className="flex-shrink-0 p-1 rounded-md text-white/80 hover:text-white hover:bg-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-white/50"
          aria-label="Dismiss notification"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </Transition>
  )
}

export default Toast
